import { MenuItem, TextField } from '@mui/material';
import type { V2DropdownElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';

/**
 * HRR v2 dropdown renderer (BE kind: "select").
 *
 * Mirrors the v1 Portal dropdown look-and-feel by leaning on MUI's
 * `<TextField select>`, which picks up the shared theme overrides
 * (border, label, helper-text colors) used by every other text input
 * in HRR. The picker shows option `label`s; selecting one calls
 * `onChange(opt.key)` — same wire format as the card-style singleSelect,
 * so the BE answerPage validator is agnostic to which renderer was used.
 *
 * Used for: per-channel monetary ranges (slides 85–87 of the HRR design),
 * monthly volume picker, and any future bounded discrete-value question
 * where stacking cards would be too tall.
 *
 * data-cy: `hrr-element-${elementId}` on the input, `hrr-option-${elementId}-${key}`
 * on each MenuItem — keeps E2E selector parity with SingleSelectElement.
 */
export function DropdownElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2DropdownElement>) {
	const stringValue = typeof value === 'string' ? value : '';
	const errorMessage = errors[0]?.message;
	const hasError = errors.length > 0;
	// Stable id linking the input to its error helper text for assistive tech (RND-4285).
	const errId = `hrr-error-${element.elementId}`;

	return (
		<TextField
			select
			fullWidth
			value={stringValue}
			onChange={(e) => onChange(e.target.value)}
			label={element.label}
			helperText={errorMessage ?? element.description}
			error={hasError}
			required={element.required}
			disabled={disabled}
			FormHelperTextProps={{ id: errId }}
			SelectProps={{
				displayEmpty: false,
				inputProps: {
					'aria-invalid': hasError,
					'aria-describedby': hasError ? errId : undefined,
					'data-cy': `hrr-element-${element.elementId}`,
					'data-element-type': 'dropdown',
				},
			}}>
			{element.placeholder && (
				<MenuItem value="" disabled>
					{element.placeholder}
				</MenuItem>
			)}
			{element.options.map((opt) => (
				<MenuItem
					key={opt.key}
					value={opt.key}
					data-cy={`hrr-option-${element.elementId}-${opt.key}`}>
					{opt.label}
				</MenuItem>
			))}
		</TextField>
	);
}
